import { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { api } from "./api";
import { Shell } from "./components/shell";
import { LoadingBlock } from "./components/ui";
import { AuditPage } from "./pages/audit";
import { AutoDormCheckPage } from "./pages/auto-dorm-check";
import { DataPage } from "./pages/data";
import { JobsPage } from "./pages/jobs";
import { LoginPage } from "./pages/login";
import { OverviewPage } from "./pages/overview";
import { PublicationsPage } from "./pages/publications";
import { UsersPage } from "./pages/users";
import type { AdminAuthMode, AdminIdentity, RouteKey } from "./types";

function routeFromHash(): RouteKey {
  const route = window.location.hash.replace(/^#\/?/, "") as RouteKey;
  return [
    "overview",
    "users",
    "publications",
    "auto-dorm-check",
    "data",
    "jobs",
    "audit",
  ].includes(route)
    ? route
    : "overview";
}

export default function App() {
  const [admin, setAdmin] = useState<AdminIdentity | null>(null);
  const [authMode, setAuthMode] = useState<AdminAuthMode | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [route, setRoute] = useState<RouteKey>(routeFromHash);

  useEffect(() => {
    void Promise.allSettled([api.authMode(), api.session()]).then(
      ([modeResult, sessionResult]) => {
        setAuthMode(
          modeResult.status === "fulfilled" ? modeResult.value.mode : "oidc",
        );
        setAdmin(
          sessionResult.status === "fulfilled"
            ? sessionResult.value.admin
            : null,
        );
        setCheckingSession(false);
      },
    );
  }, []);

  useEffect(() => {
    const onHashChange = () => setRoute(routeFromHash());
    const onUnauthorized = () => {
      setAdmin(null);
    };
    window.addEventListener("hashchange", onHashChange);
    window.addEventListener("easy-swu:admin-unauthorized", onUnauthorized);
    return () => {
      window.removeEventListener("hashchange", onHashChange);
      window.removeEventListener("easy-swu:admin-unauthorized", onUnauthorized);
    };
  }, []);

  const navigate = (nextRoute: RouteKey) => {
    window.location.hash = `/${nextRoute}`;
    setRoute(nextRoute);
  };

  const logout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    if (authMode === "oidc") {
      window.location.assign(api.ssoLogoutUrl());
      return;
    }
    try {
      await api.logout();
      setAdmin(null);
    } catch {
      toast.error("退出失败，请重试");
    } finally {
      setLoggingOut(false);
    }
  };

  if (checkingSession) {
    return (
      <main className="boot-screen">
        <div className="boot-card">
          <div className="brand-mark brand-mark--large" aria-hidden="true">
            <span />
            <span />
          </div>
          <strong>Easy SWU</strong>
          <p>正在验证管理会话</p>
          <LoadingBlock rows={2} />
        </div>
      </main>
    );
  }

  return (
    <>
      {admin ? (
        <Shell
          admin={admin}
          route={route}
          onRoute={navigate}
          onLogout={() => void logout()}
          loggingOut={loggingOut}
        >
          {route === "overview" && <OverviewPage onNavigate={navigate} />}
          {route === "users" && <UsersPage />}
          {route === "publications" && <PublicationsPage />}
          {route === "auto-dorm-check" && <AutoDormCheckPage />}
          {route === "data" && <DataPage />}
          {route === "jobs" && <JobsPage />}
          {route === "audit" && <AuditPage />}
        </Shell>
      ) : (
        <LoginPage
          mode={authMode || "oidc"}
          onAuthenticated={(identity) => {
            setAdmin(identity);
            navigate("overview");
          }}
        />
      )}
      <Toaster
        richColors
        closeButton
        position="top-right"
        toastOptions={{ duration: 3800 }}
      />
    </>
  );
}
